import React from 'react';
import { CustomIcon } from '../../../components/UI';
import './LoadHistoryEventCard.scss';

/**
 * Load History Event Card Component
 * Displays individual load history event with change details
 */
const LoadHistoryEventCard = ({ event, isExpanded, onToggle }) => {
  const {
    createdAt,
    action,
    actor,
    changes = []
  } = event;

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = () => {
    switch (action) {
      case 'created':
        return 'plus';
      case 'updated':
        return 'edit-03';
      case 'status_update':
        return 'activity';
      case 'assign':
        return 'user-plus';
      case 'delete':
        return 'trash-03';
      default:
        return 'activity';
    }
  };

  const getActionColor = () => {
    switch (action) {
      case 'created':
        return '#34d399';
      case 'updated':
        return '#60a5fa';
      case 'status_update':
        return '#fbbf24';
      case 'assign':
        return '#a78bfa';
      case 'delete':
        return '#f97316';
      default:
        return '#6b7280';
    }
  };

  const getActionLabel = () => {
    switch (action) {
      case 'created':
        return 'Created';
      case 'updated':
        return 'Updated';
      case 'status_update':
        return 'Status Changed';
      case 'assign':
        return 'Assigned';
      case 'delete':
        return 'Deleted';
      default:
        return action.replace('_', ' ');
    }
  };

  const getActorName = () => {
    if (!actor) return 'Unknown user';
    if (actor.actorId) {
      const user = typeof actor.actorId === 'object' ? actor.actorId : null;
      if (user) {
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
        return name || user.email || user.companyName || 'Unknown user';
      }
    }
    return actor.actorEmail || 'Unknown user';
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return value.length > 0 ? `${value.length} items` : 'Empty';
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <div className="load-history-event-card">
      <div className="load-history-event-card__header" onClick={onToggle}>
        <div 
          className="load-history-event-card__icon" 
          style={{ backgroundColor: `${getActionColor()}20` }}
        >
          <CustomIcon 
            name={getActionIcon()} 
            size={20} 
            style={{ color: getActionColor() }} 
          />
        </div>
        <div className="load-history-event-card__info">
          <div className="load-history-event-card__title">
            <span className="load-history-event-card__action">{getActionLabel()}</span>
          </div>
          <div className="load-history-event-card__meta">
            <span>{formatDate(createdAt)}</span>
            <span className="load-history-event-card__separator">•</span>
            <span>by {getActorName()}</span>
            {actor?.actorRole && (
              <>
                <span className="load-history-event-card__separator">•</span>
                <span className="load-history-event-card__role">{actor.actorRole}</span>
              </>
            )}
          </div>
        </div>
        <div className="load-history-event-card__toggle">
          <CustomIcon
            name={isExpanded ? 'up' : 'down1'}
            size={16}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="load-history-event-card__body">
          {changes.length > 0 ? (
            <div className="load-history-event-card__changes">
              <div className="load-history-event-card__changes-header">
                <strong>Changes ({changes.length}):</strong>
              </div>
              {changes.map((change, index) => (
                <div key={index} className="load-history-event-card__change">
                  <div className="load-history-event-card__change-field">
                    <strong>{change.field}:</strong>
                  </div>
                  <div className="load-history-event-card__change-values">
                    <span className="load-history-event-card__change-from">
                      {formatValue(change.from)}
                    </span>
                    <span className="load-history-event-card__change-arrow">→</span>
                    <span className="load-history-event-card__change-to">
                      {formatValue(change.to)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="load-history-event-card__no-changes">
              No detailed changes available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoadHistoryEventCard;
